import React, { Component } from "react";
import React3 from "react-three-renderer";
import * as THREE from "three";
import ReactResizeDetector from "react-resize-detector";
import idx from "idx";
import { CSSTransitionGroup } from "react-transition-group";

import { EDITOR_ITEMS, WIDGETS } from "../../constants";
import JumperCamera from "./PlayerCamera";
import JumperBackground from "./PlayerBackground";
import PlayerWidget from "./PlayerWidget";
import CSS3DWorld from "./CSS3DWorld";
import PlayerPortal from "./PlayerPortal";
import PlayerInfoIcon from "./PlayerInfoIcon";
import PlayerInfoWindow from "./PlayerInfoWindow";
import LoadingOverlay from "../../components/LoadingOverlay";
import PlayerMenu from "./PlayerMenu";
import PlayerMinimap from "./PlayerMinimap";
import WidgetGizmo from "./WidgetGizmo";
import PlayerRelatedMenu from "./PlayerRelatedMenu";
import "./player.css";
import BrandingLogo from "./BrandingLogo/BrandingLogo";

class Player extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.frameId = null;
    this.autoRotateTimeout;
    
    const firstSceneId =
      props.project.scenes && Object.keys(props.project.scenes).length > 0
        ? Object.keys(props.project.scenes)[0]
        : null;

    var defaultTheta = props.project.camera_defaults_theta ? parseFloat(props.project.camera_defaults_theta) : 30;
    var defaultPhi = props.project.camera_defaults_phi ? parseFloat(props.project.camera_defaults_phi) : -10;

    this.defaultValues = {
      cameraGamma: 0,
      cameraPhi: defaultPhi,
      cameraTheta: defaultTheta,
      cameraFov: 80,
      cameraCurrentGamma: 0,
      cameraCurrentPhi: defaultPhi,
      cameraCurrentTheta: defaultTheta,
      cameraCurrentFov: 80
    };

    this.state = {
      object: props.project,
      autoRotate: null,
      autoRotateSpeed: 0,
      autoRotateTimeout: 10000,
      windowWidth: 0,
      windowHeight: 0,

      sceneId: props.sceneId || firstSceneId,
      layerId: props.layerId || "_generated",
      sceneDirection: new THREE.Vector3(0, 0, 0),
      sceneDistance: 0,

      perspectiveCamera: null,
      cameraGamma: this.defaultValues.cameraGamma,
      cameraPhi: this.defaultValues.cameraPhi,
      cameraTheta: this.defaultValues.cameraTheta,
      cameraFov: this.defaultValues.cameraFov,
      cameraCurrentGamma: this.defaultValues.cameraCurrentGamma,
      cameraCurrentPhi: this.defaultValues.cameraCurrentPhi,
      cameraCurrentTheta: this.defaultValues.cameraCurrentTheta,
      cameraCurrentFov: this.defaultValues.cameraCurrentFov,
      cameraThetaOffset: 0,
      hasGyroscope: false,
      useGyroscope: false,
      widgets: [],
      layers: [],
      showWidgets: false,
      imageLoaded: false,
      imageError: false,
      loaded: false,
      showHotspots: true,
      showMinimap: false
    };

    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onWheel = this.onWheel.bind(this);
    this.cameraOnMove = this.cameraOnMove.bind(this);
    this.onClickPortal = this.onClickPortal.bind(this);
    this.onRelatedClick = this.onRelatedClick.bind(this);
    this.onResize = this.onResize.bind(this);
    this.loop = this.loop.bind(this);
    this.disableAutoRotate = this.disableAutoRotate.bind(this);
    this.restoreAutoRotate = this.restoreAutoRotate.bind(this);

    this.widgetOnMouseDown = this.widgetOnMouseDown.bind(this);
    this.widgetOnMouseMove = this.widgetOnMouseMove.bind(this);
    this.widgetOnMouseUp = this.widgetOnMouseUp.bind(this);

    this.getCameraRef = this.getCameraRef.bind(this);

    this.mouseDown = false;
    this.onImageLoad = this.onImageLoad.bind(this);
    this.onImageError = this.onImageError.bind(this);

    this.toggleMinimap = this.toggleMinimap.bind(this);
    this.toggleHotSpots = this.toggleHotSpots.bind(this);
    this.toggleFullScreen = this.toggleFullScreen.bind(this);
    this.toggleGyroscope = this.toggleGyroscope.bind(this);
    this.onMinimapPortalClicked = this.onMinimapPortalClicked.bind(this);

    this.backgroundOnComplete = this.backgroundOnComplete.bind(this);

    this.onFullscrrenChange = this.onFullscrrenChange.bind(this);
    this.onScreenOrientationChangeEvent = this.onScreenOrientationChangeEvent.bind(
      this
    );
    this.onDeviceOrientationChangeEvent = this.onDeviceOrientationChangeEvent.bind(
      this
    );
    document.addEventListener("fullscreenchange", this.onFullscrrenChange);
    document.addEventListener(
      "webkitfullscreenchange",
      this.onFullscrrenChange
    );
    document.addEventListener("mozfullscreenchange", this.onFullscrrenChange);
    document.addEventListener("MSFullscreenChange", this.onFullscrrenChange);

    if (window.DeviceOrientationEvent) {
      window.addEventListener(
        "orientationchange",
        this.onScreenOrientationChangeEvent,
        false
      );
      window.addEventListener(
        "deviceorientation",
        this.onDeviceOrientationChangeEvent,
        false
      );
    }
    this.thetaOffset = 0;
  }

  onScreenOrientationChangeEvent(event) {
    this.screenOrientation = window.orientation || 0;
    this.onResize();
  }

  onDeviceOrientationChangeEvent(event) {
    var alpha = event.alpha ? THREE.Math.degToRad(event.alpha) : 0;
    var beta = event.beta ? THREE.Math.degToRad(event.beta) : 0;
    var gamma = event.gamma ? THREE.Math.degToRad(event.gamma) : 0;
    var orient = this.screenOrientation
      ? THREE.Math.degToRad(this.screenOrientation)
      : 0;
    var zee = new THREE.Vector3(0, 0, 1);
    var euler = new THREE.Euler().set(beta, alpha, -gamma, "YXZ");
    var q0 = new THREE.Quaternion();
    var q1 = new THREE.Quaternion(-Math.sqrt(0.5), 0, 0, Math.sqrt(0.5));
    var quaternion = new THREE.Quaternion().setFromEuler(euler);
    quaternion.multiply(q1);
    quaternion.multiply(q0.setFromAxisAngle(zee, -orient));
    var angles = new THREE.Euler().setFromQuaternion(quaternion, "YXZ");
    if (event.alpha) {
      this.setState({
        hasGyroscope: true
      });
    }

    if (this.state.useGyroscope) {
      this.setState({
        cameraGamma: THREE.Math.radToDeg(angles.z),
        cameraPhi: THREE.Math.radToDeg(angles.x),
        cameraTheta:
          THREE.Math.radToDeg(angles.y) + this.state.cameraThetaOffset
      });
    } else {
      this.thetaOffset = THREE.Math.radToDeg(angles.y);
    }
  }

  getCameraRef(camera) {
    this.setState({
      perspectiveCamera: camera
    });
  }

  componentDidMount() {
    this.componentWillReceiveProps(this.props);

    var searchParams = new URLSearchParams(window.location.search);
    
    if (searchParams.get("autorotate") !== null) {
      let autoRotateSpeed = Number(searchParams.get("autorotate"));
      
      this.setState({autoRotate: true, autoRotateSpeed});

      if (searchParams.get("timeout") !== null) {
        this.setState({autoRotateTimeout: Number(searchParams.get("timeout"))});
      }
      
      if( !this._frameId ) {
        this._frameId = window.requestAnimationFrame( this.loop );
      }
    }
  }

  loop() {
    if (this.state.autoRotate) {
      this.setState({
        cameraTheta: (this.state.cameraTheta + this.state.autoRotateSpeed % 360)
      });
    }
    
    this.frameId = window.requestAnimationFrame( this.loop );
  }


  componentWillUnmount() {
    window.cancelAnimationFrame( this._frameId );
  }

  disableAutoRotate() {
    if (this.state.autoRotate) {
      if (this.autoRotateTimeout) {
        clearTimeout(this.autoRotateTimeout);
      }

      this.setState({autoRotate: false});

      this.autoRotateTimeout = setTimeout(() => {
        this.restoreAutoRotate();
      }, this.state.autoRotateTimeout );
    }
  }

  restoreAutoRotate() {
    this.setState({autoRotate: true});
  }

  componentWillReceiveProps(nextProps) {
    const firstSceneId =
      nextProps.project.scenes &&
      Object.keys(nextProps.project.scenes).length > 0
        ? Object.keys(nextProps.project.scenes)[0]
        : null;

    const sceneId = nextProps.sceneId || firstSceneId;
    const layerId = nextProps.layerId || "_generated";
    const layers = idx(nextProps, _ => _.project.scenes[sceneId].layers) || {};
    const widgets =
      idx(nextProps, _ => _.project.scenes[sceneId].layers[layerId].widgets) ||
      {};
    const { downloadURL: background = "" } =
      idx(this.state.object, _ => _.scenes[this.state.sceneId].panorama) || {};

    const { downloadURL: backgroundNew = "" } =
      idx(this.state.object, _ => _.scenes[sceneId].panorama) || {};
    this.setState({
      windowWidth: this.app ? this.app.clientWidth : 0,
      windowHeight: this.app ? this.app.clientHeight : 0,
      sceneId: nextProps.sceneId || firstSceneId,
      layerId,
      object: nextProps.project,
      layers,
      widgets,
      imageLoaded: background === backgroundNew && this.state.loaded,
      editor: nextProps.editor || false
    });
    this.fixWidgetsPositions();
  }

  onImageLoad() {
    this.setState({ imageLoaded: true, loaded: true });
  }

  onImageError() {
    this.setState({ imageError: true });
  }

  onMouseDown(event) {
    this.app.addEventListener("mousemove", this.onMouseMove);
    this.app.addEventListener("mouseup", this.onMouseUp);
    event.preventDefault();
    this.mouseDown = true;
    this.mouseX = event.nativeEvent.clientX;
    this.mouseY = event.nativeEvent.clientY;
    this.disableAutoRotate();
  }

  onTouchStart(event) {
    this.app.addEventListener("touchmove", this.onTouchMove, {
      passive: false
    });
    this.app.addEventListener("touchend", this.onTouchEnd, { passive: false });
    this.mouseDown = true;
    this.mouseX = event.nativeEvent.touches[0].clientX;
    this.mouseY = event.nativeEvent.touches[0].clientY;
    this.disableAutoRotate();
  }

  onMouseMove(event) {
    if (!this.widget) {
      event.preventDefault();
      if (this.mouseDown) {
        this.setState({
          cameraPhi: THREE.Math.clamp(
            parseFloat(
              this.state.cameraPhi - (this.mouseY - event.clientY) * 0.1
            ),
            -90,
            90
          ),
          cameraTheta:
            this.state.cameraTheta - (this.mouseX - event.clientX) * 0.1
        });
        this.mouseX = event.clientX;
        this.mouseY = event.clientY;
      }
    }
  }

  onTouchMove(event) {
    if (!this.widget) {
      event.preventDefault();
      if (this.mouseDown) {
        this.setState({
          cameraPhi: THREE.Math.clamp(
            parseFloat(
              this.state.cameraPhi -
                (this.mouseY - event.touches[0].clientY) * 0.1
            ),
            -90,
            90
          ),
          cameraTheta:
            this.state.cameraTheta -
            (this.mouseX - event.touches[0].clientX) * 0.1
        });
        this.mouseX = event.touches[0].clientX;
        this.mouseY = event.touches[0].clientY;
      }
    }
  }

  onMouseUp(event) {
    //event.preventDefault();
    this.app.removeEventListener("mousemove", this.onMouseMove);
    this.app.removeEventListener("mouseup", this.onMouseUp);
  }

  onTouchEnd(event) {
    //event.preventDefault();
    this.app.addEventListener("touchmove", this.onTouchMove);
    this.app.addEventListener("touchend", this.onTouchEnd);
  }

  onWheel(event) {
    // blocked fov change for now
    /*this.setState({
      cameraFov: THREE.Math.clamp(
        parseFloat(this.state.cameraFov + event.nativeEvent.deltaY * 0.03),
        10,
        100
      )
    });*/
  }

  onResize() {
    if (this.app) {
      this.setState({
        windowWidth: this.app.clientWidth,
        windowHeight: this.app.clientHeight
      });
    }
  }

  onClickPortal(portal) {
    if (!this.state.editor) {
      var url;
      if (this.props.match.url.indexOf("embed") == 1) {
        url = `/embed/${this.props.match.params.menuBottom}/${
          this.props.match.params.mapBottom
        }/${this.props.projectId}/${portal.targetScene}`;
      } else {
        url = `/${this.props.projectId}/${portal.targetScene}`;
      }
      this.setState(
        {
          sceneId: portal.targetScene,
          sceneURL: this.props.project.scenes[portal.targetScene].panorama,
          sceneDirection: new THREE.Vector3().setFromSpherical(
            new THREE.Spherical(
              0.5,
              THREE.Math.degToRad(portal.phi),
              THREE.Math.degToRad(portal.theta)
            )
          ),
          sceneDistance: 0.5,
          imageLoaded: false,
          loaded: false,
          showWidgets: false
        },
        this.props.push(url)
      );
    }
  }

  onRelatedClick(sourceUrl) {
    if (!this.state.editor) {
      this.props.push(`/${sourceUrl}`);
    }
  }

  onMinimapPortalClicked(sceneId) {
    var url;
    if (this.props.match.url.indexOf("embed") == 1) {
      url = `/embed/${this.props.match.params.menuBottom}/${
        this.props.match.params.mapBottom
      }/${this.props.projectId}/${sceneId}`;
    } else {
      url = `/${this.props.projectId}/${sceneId}`;
    }
    this.setState({
      sceneId,
      sceneURL: this.props.project.scenes[sceneId].panorama,
      sceneDirection: new THREE.Vector3().setFromSpherical(
        new THREE.Spherical(0, 0, 0)
      ),
      sceneDistance: 0,
      imageLoaded: false,
      loaded: false,
      showWidgets: false
    });

    this.props.push(url);
  }

  widgetOnMouseDown(event, index) {
    if (this.state.editor) {
      this.app.addEventListener("mousemove", this.widgetOnMouseMove);
      this.app.addEventListener("mouseup", this.widgetOnMouseUp);
      this.widget = index;
      this.widgetPhi = this.state.widgets[this.widget].phi || 90;
      this.widgetTheta = this.state.widgets[this.widget].theta || 0;
      this.positionX = event.nativeEvent.clientX;
      this.positionY = event.nativeEvent.clientY;
      this.props.selectInspectorItem(index, EDITOR_ITEMS.WIDGET);
    }
  }

  widgetOnMouseMove(event) {
    if (this.widget) {
      const bounds = this.div.getBoundingClientRect();
      const positionX =
        ((event.clientX - bounds.x) / this.state.windowWidth) * 2 - 1;
      const positionY =
        (-(event.clientY - bounds.y) / this.state.windowHeight) * 2 + 1;
      const prevPositionX =
        ((this.positionX - bounds.x) / this.state.windowWidth) * 2 - 1;
      const prevPositionY =
        (-(this.positionY - bounds.y) / this.state.windowHeight) * 2 + 1;
      const position = new THREE.Vector3(positionX, positionY, 1).unproject(
        this.state.perspectiveCamera
      );
      const prevPosition = new THREE.Vector3(
        prevPositionX,
        prevPositionY,
        1
      ).unproject(this.state.perspectiveCamera);
      const sPosition = new THREE.Spherical().setFromVector3(position);
      const sPrevPosition = new THREE.Spherical().setFromVector3(prevPosition);
      const movementTheta =
        ((sPosition.theta - sPrevPosition.theta) / Math.PI) * 180;
      const movementPhi = ((sPosition.phi - sPrevPosition.phi) / Math.PI) * 180;
      this.state.widgets[this.widget].theta = this.widgetTheta + movementTheta;
      this.state.widgets[this.widget].phi = THREE.Math.clamp(
        this.widgetPhi + movementPhi,
        0,
        180
      );
      this.forceUpdate();
    }
  }

  widgetOnMouseUp() {
    if (this.props.onWidgetMoveEnd) {
      this.props.onWidgetMoveEnd(
        this.widget,
        this.state.widgets[this.widget].phi,
        this.state.widgets[this.widget].theta
      );
    }

    this.widget = null;
    this.app.removeEventListener("mousemove", this.widgetOnMouseMove);
    this.app.removeEventListener("mouseup", this.widgetOnMouseUp);
  }

  cameraOnMove(rotation, fov) {
    this.setState({
      cameraCurrentGamma: THREE.Math.radToDeg(rotation.x),
      cameraCurrentPhi: -THREE.Math.radToDeg(rotation.x) + 90,
      cameraCurrentTheta: THREE.Math.radToDeg(rotation.y),
      cameraCurrentFov: fov
    });
  }

  screenToSpherical(clientX, clientY) {
    const bounds = this.div.getBoundingClientRect();
    const positionX = ((clientX - bounds.x) / this.state.windowWidth) * 2 - 1;
    const positionY = (-(clientY - bounds.y) / this.state.windowHeight) * 2 + 1;
    const position = new THREE.Vector3(positionX, positionY, 1).unproject(
      this.state.perspectiveCamera
    );
    const sPosition = new THREE.Spherical().setFromVector3(position);
    const theta = (sPosition.theta / Math.PI) * 180;
    const phi = (sPosition.phi / Math.PI) * 180;
    return { phi: phi, theta: theta };
  }

  fixWidgetsPositions() {
    if (this.state.layers) {
      Object.entries(this.state.layers).map(([index, layer]) => {
        if (layer.widgets) {
          Object.entries(layer.widgets).map(([index, widget]) => {
            if (!widget.phi || !widget.theta) {
              if (widget.dropPosition) {
                const position = this.screenToSpherical(
                  widget.dropPosition.x,
                  widget.dropPosition.y
                );
                widget.phi = position.phi;
                widget.theta = position.theta;
              } else {
                widget.phi = this.state.cameraCurrentPhi;
                widget.theta = this.state.cameraCurrentTheta + 180;
              }

              if (this.props.onWidgetMoveEnd) {
                this.props.onWidgetMoveEnd(index, widget.phi, widget.theta);
              }
            }
          });
        }
      });
    }
  }

  toggleMinimap() {
    this.setState({ showMinimap: !this.state.showMinimap });
  }

  toggleHotSpots() {
    this.setState({ showHotspots: !this.state.showHotspots });
  }

  toggleGyroscope() {
    this.setState({
      useGyroscope: !this.state.useGyroscope,
      cameraThetaOffset: this.state.cameraCurrentTheta - this.thetaOffset,
      cameraGamma: 0
    });
  }

  toggleFullScreen() {
    // go full-screen
    if (!this.state.fullscreen) {
      if (this.app.requestFullscreen) {
        this.app.requestFullscreen();
      } else if (this.app.webkitRequestFullscreen) {
        this.app.webkitRequestFullscreen();
      } else if (this.app.mozRequestFullScreen) {
        this.app.mozRequestFullScreen();
      } else if (this.app.msRequestFullscreen) {
        this.app.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }
  }

  onFullscrrenChange(event) {
    this.onResize();
    this.setState({ fullscreen: !this.state.fullscreen });
  }

  backgroundOnComplete() {
    this.setState({
      showWidgets: true
    });
  }

  renderRelatedMenu() {
    if (this.props.project.related) {
      return (
        <PlayerRelatedMenu
          onClick={this.onRelatedClick} 
          color={this.props.project.color || "#000000"}
          items={this.props.project.related} 
        />
      );
    }
  }

  renderBrandingLogo() {
    if (this.props.project.branding_logo) {
      return (
        <BrandingLogo src={this.props.project.branding_logo.downloadURL} />
      );
    }
  }

  renderPlayerMenu() {
    if (this.props.project.toolbarMode === "hidden")
      return null;

    let menuBottom;
    let menuBottomStyle = { bottom: "20px" };
    let mapBottom;
    let mapBottomStyle = {};
    if (this.props.match.url.indexOf("embed") == 1) {
      menuBottom = this.props.match.params.menuBottom + "px";
      menuBottomStyle = { bottom: menuBottom };
      mapBottom = this.props.match.params.mapBottom + "px";
      mapBottomStyle = {
        borderBottom: mapBottom + " solid rgba(0, 0, 0, 0.5)"
      };
    } else if (window.frameElement) {
      if (window.frameElement.getAttribute("data-menuBottom")) {
        menuBottom = window.frameElement.getAttribute("data-menuBottom");
        menuBottomStyle = { bottom: menuBottom };
      }
      if (window.frameElement.getAttribute("data-mapBottom")) {
        mapBottom = window.frameElement.getAttribute("data-mapBottom");
        mapBottomStyle = {
          borderBottom: mapBottom + " solid rgba(0, 0, 0, 0.5)"
        };
      }
    }
    return (
      <div>
        <PlayerMenu
          project={this.props.project}
          color={this.props.project.color || "#000000"}
          bottomStyle={menuBottomStyle}
          toggleMinimap={this.toggleMinimap}
          isMinimapShown={this.state.showMinimap}
          toggleHotSpots={this.toggleHotSpots}
          isHotSpotsShown={this.state.showHotspots}
          fullscreen={this.state.fullscreen}
          toggleFullScreen={this.toggleFullScreen}
          toggleGyroscope={this.toggleGyroscope}
          hasGyroscope={this.state.hasGyroscope}
          useGyroscope={this.state.useGyroscope}
        />
        <PlayerMinimap
          menuBottom={menuBottomStyle}
          bottomStyle={mapBottomStyle}
          windowWidth={this.state.windowWidth}
          windowHeight={this.state.windowHeight}
          project={this.props.project}
          show={this.state.showMinimap}
          match={this.props.match}
          onPortalClick={this.onMinimapPortalClicked}
        />
      </div>
    );
  }

  render() {
    const { downloadURL: background = "" } =
      idx(this.state.object, _ => _.scenes[this.state.sceneId].panorama) || {};
    let content = (
      <div
        className="Player"
        role="presentation"
        onMouseDown={this.onMouseDown}
        onTouchStart={this.onTouchStart}
        onWheel={this.onWheel}
        ref={component => {
          this.div = component;
        }}
        style={{
          width: "100%",
          height: "100%",
          position: "absolute"
        }}
      >
        <React3
          width={this.state.windowWidth}
          height={this.state.windowHeight}
          mainCamera="camera"
        >
          <scene>
            <JumperCamera
              ref={component => {
                this.camera = component;
              }}
              gamma={this.state.cameraGamma}
              phi={this.state.cameraPhi} /* [-90, 90] */
              theta={this.state.cameraTheta} /* [-180, 180] */
              delay={this.state.useGyroscope ? 10 : 20} /* [0, inf] */
              fov={this.state.cameraFov} /* [1, 180] */
              aspect={this.state.windowWidth / this.state.windowHeight}
              onMove={this.cameraOnMove}
              cameraRef={this.getCameraRef}
            />
            <JumperBackground
              url={background}
              direction={this.state.sceneDirection}
              distance={this.state.sceneDistance}
              onImageLoad={this.onImageLoad}
              onImageError={this.onImageError}
              onComplete={this.backgroundOnComplete}
            />
          </scene>
        </React3>
        {this.state.imageLoaded && (
          <CSS3DWorld
            cameraFov={this.state.cameraCurrentFov}
            windowHeight={this.state.windowHeight}
          >
            {this.state.layers &&
              this.state.showWidgets &&
              Object.entries(this.state.layers).map(([layerId, layer]) => {
                if (layer.widgets) {
                  return Object.entries(layer.widgets).map(
                    ([widgetId, widget]) => {
                      return (
                        <PlayerWidget
                          key={widgetId}
                          index={widgetId}
                          object={widget}
                          phi={widget.phi}
                          theta={widget.theta}
                          rotation={widget.rotation || "0"}
                          scale={1}
                          windowWidth={this.state.windowWidth}
                          windowHeight={this.state.windowHeight}
                          perspectiveCamera={this.state.perspectiveCamera}
                          cameraFov={this.state.cameraCurrentFov}
                        >
                          {this.props.inEditor && (
                            <WidgetGizmo
                              key={widgetId}
                              index={widgetId}
                              onMouseDown={this.widgetOnMouseDown}
                              inEditor={this.props.inEditor}
                              object={widget}
                            />
                          )}
                          {widget.type === "portal" ? (
                            <PlayerPortal
                              index={widgetId}
                              object={widget}
                              onClick={this.onClickPortal}
                            />
                          ) : widget.type === "info_window" &&
                          (this.state.showHotspots || this.props.inEditor) ? (
                            <PlayerInfoIcon
                              index={widgetId}
                              object={widget}
                              prefab={
                                this.props.project.prefabs[widget.prefabId]
                              }
                            />
                          ) : (
                            ""
                          )}
                        </PlayerWidget>
                      );
                    }
                  );
                }
              })}
          </CSS3DWorld>
        )}
        <div>
          {this.state.layers &&
            this.state.showWidgets &&
            Object.entries(this.state.layers).map(([layerId, layer]) => {
              if (layer.widgets) {
                return Object.entries(layer.widgets).map(
                  ([widgetId, widget]) => {
                    return (
                      widget.type === "info_window" &&
                      (this.state.showHotspots || this.props.inEditor) && (
                        <PlayerInfoWindow
                          index={widgetId}
                          object={widget}
                          prefab={this.props.project.prefabs[widget.prefabId]}
                          phi={widget.phi}
                          theta={widget.theta}
                          windowWidth={this.state.windowWidth}
                          windowHeight={this.state.windowHeight}
                          perspectiveCamera={this.state.perspectiveCamera}
                          cameraFov={this.state.cameraCurrentFov}
                        />
                      )
                    );
                  }
                );
              }
            })}
        </div>
        {!this.state.imageLoaded && (
          <LoadingOverlay
            style={{ background: "rgba(255, 255, 255, 0)", zIndex: 999 }}
          />
        )}
      </div>
    );

    if (this.state.imageError) {
      content = <div>Error loading image</div>;
    }

    if (!background || typeof background !== "string") {
      content = null;
    }
    return (
      <div
        className="App"
        role="presentation"
        ref={app => {
          this.app = app;
        }}
      >
        {content}
        {!this.props.inEditor && this.renderPlayerMenu()}
        {!this.props.inEditor && this.renderRelatedMenu()}
        {!this.props.inEditor && this.renderBrandingLogo()}
        {
          <ReactResizeDetector
            handleWidth
            handleHeight
            onResize={this.onResize}
          />
        }
      </div>
    );
  }
}

export default Player;



// WEBPACK FOOTER //
// ./src/components/Player/Player.js